import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { PERSON_PROFICIENCY_LEVELS } from "@releve/shared";
import { AutocompleteCombobox } from "@/components/ui/autocomplete-combobox";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  StandardDialog,
  StandardDialogBody,
  StandardDialogContent,
  StandardDialogDescription,
  StandardDialogFooter,
  StandardDialogHeader,
  StandardDialogTitle,
} from "@/components/ui/standard-dialog";
import { Textarea } from "@/components/ui/textarea";
import { skillKeys, useCreateSkill } from "@/hooks/useSkills";
import { useTranslation } from "@/hooks/useTranslation";
import { formatLocalISODate } from "@/lib/dateUtils";
import { SKILL_CATEGORIES, type Skill } from "@/types/people";
import { LabelWithError } from "./person-form/fields";
import type { SkillDraft } from "./person-form/personFormSchema";

const createSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().trim().min(1, t("form.skill.nameRequired")),
    category: z.string(),
    description: z.string(),
    color: z.string(),
    proficiency_level: z.enum(PERSON_PROFICIENCY_LEVELS),
    years_experience: z.number().int().min(0).max(50).nullable(),
    notes: z.string(),
  });

type CreateSkillValues = z.infer<ReturnType<typeof createSchema>>;

interface CreateSkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** The org's skills — a typed name that matches one is reused, not duplicated. */
  skills: Skill[];
  /** The skill to add to the person, with the proficiency entered here. */
  onCreated: (draft: SkillDraft) => void;
}

/**
 * Create a skill in the org vocabulary (or reuse one of the same name) and
 * add it to the person being edited. Mount it only while open. The submit
 * handler stops propagation — React submit events bubble through the portal
 * to the person form around it.
 */
export function CreateSkillDialog({ open, onOpenChange, organizationId, skills, onCreated }: CreateSkillDialogProps) {
  const { t } = useTranslation("people");
  const queryClient = useQueryClient();
  const createSkill = useCreateSkill();
  const schema = useMemo(() => createSchema(t), [t]);

  const form = useForm<CreateSkillValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      category: "",
      description: "",
      color: "#6B7280",
      proficiency_level: "intermediate",
      years_experience: null,
      notes: "",
    },
  });

  const findByName = (list: Skill[], name: string) =>
    list.find((skill) => skill.name.toLowerCase() === name.trim().toLowerCase());

  const onSubmit = async (values: CreateSkillValues) => {
    let skill = findByName(skills, values.name);
    if (!skill) {
      try {
        skill = await createSkill.mutateAsync({
          name: values.name.trim(),
          category: values.category || undefined,
          description: values.description.trim() || undefined,
          color: values.color || undefined,
          organization_id: organizationId,
        });
      } catch (error) {
        // A concurrent create of the same name: reuse the row that won.
        if ((error as { code?: string } | null)?.code === "23505") {
          await queryClient.refetchQueries({ queryKey: skillKeys.list(organizationId) });
          skill = findByName(queryClient.getQueryData<Skill[]>(skillKeys.list(organizationId)) ?? [], values.name);
        }
        // Otherwise useCreateSkill has toasted and the MutationCache reported it.
        if (!skill) return;
      }
    }

    onCreated({
      skill_id: skill.id,
      name: skill.name,
      category: skill.category ?? "",
      proficiency_level: values.proficiency_level,
      years_experience: values.years_experience,
      last_used: formatLocalISODate(new Date()),
      notes: values.notes.trim(),
    });
    onOpenChange(false);
  };

  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="sm">
        <StandardDialogHeader>
          <StandardDialogTitle>{t("form.skill.createTitle")}</StandardDialogTitle>
          <StandardDialogDescription>{t("form.skill.createDescription")}</StandardDialogDescription>
        </StandardDialogHeader>
        <Form {...form}>
          <form
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit(onSubmit)(e);
            }}
          >
            <StandardDialogBody className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <LabelWithError label={t("form.skill.name")} error={fieldState.error?.message} />
                    <FormControl>
                      <AutocompleteCombobox
                        options={skills.map((skill) => ({
                          id: skill.id,
                          name: skill.name,
                          value: skill.name,
                          color: skill.color,
                          badge: skill.category ?? undefined,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={t("form.skill.namePlaceholder")}
                        emptyMessage={t("form.skill.nameEmpty")}
                        allowCreate
                        createLabel={(value) => t("form.skill.createOption", { name: value })}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.skill.category")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("form.skill.categoryPlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SKILL_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {t(`form.skillCategories.${category}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.skill.color")}</FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="w-16 h-10 p-1"
                          aria-label={t("form.skill.color")}
                        />
                        <Input {...field} className="flex-1" />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.skill.description")}</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="proficiency_level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.skill.proficiency")}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PERSON_PROFICIENCY_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {t(`common:people.proficiency.${level}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="years_experience"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <LabelWithError label={t("form.skill.years")} error={fieldState.error?.message} />
                      <FormControl>
                        <NumberInput
                          min={0}
                          max={50}
                          step={1}
                          allowEmpty
                          value={field.value ?? ""}
                          onChange={(value) => field.onChange(value === "" ? null : Number(value))}
                          className={fieldState.error ? "border-destructive" : undefined}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.skill.notes")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </StandardDialogBody>
            <StandardDialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common:cancel")}
              </Button>
              <Button type="submit" disabled={createSkill.isPending}>
                {createSkill.isPending ? t("form.skill.creating") : t("form.skill.create")}
              </Button>
            </StandardDialogFooter>
          </form>
        </Form>
      </StandardDialogContent>
    </StandardDialog>
  );
}
